import { Outlet, useNavigate } from 'react-router-dom';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { SettingsDrawer } from '@shared/components/settings/SettingsDrawer';
import { NotificationBell } from './NotificationBell';
import { BottomTabBar } from './BottomTabBar';
import { LogOut } from 'lucide-react';

export function AppLayout() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isSuperAdmin, userProfile } = usePermissions();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Get display name from profile
  const displayName = userProfile?.display_name || user?.email;

  return (
    <SidebarProvider
      defaultOpen={true}
      style={{
        "--sidebar-width": "16rem",
        "--header-height": "4rem"
      } as React.CSSProperties}
    >
      <AppSidebar />
      <SidebarInset className="overflow-hidden">
        <header className="bg-background sticky top-0 z-10 flex h-16 shrink-0 items-center border-b px-4 lg:px-6">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="md:hidden" />
              <h1 className="text-lg font-semibold font-serif">XYZ HQ</h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground hidden md:inline">
                {displayName}
              </span>
              {isSuperAdmin && <NotificationBell />}
              {isSuperAdmin && <SettingsDrawer />}
              <ThemeToggle />
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSignOut}
                aria-label="Log out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex flex-1 flex-col overflow-hidden max-w-screen-2xl mx-auto w-full">
            <Outlet />
          </div>
        </div>
        <BottomTabBar />
      </SidebarInset>
    </SidebarProvider>
  );
}
